// UI COMPONENTS
import { Button, Container, Dropdown, Form, Table } from "react-bootstrap";
// HOOKS & ICONS
import { useNavigate } from "react-router-dom";
import { BsArrowLeft, BsPencil, BsTrash } from "react-icons/bs";
import { Controller, useForm } from "react-hook-form";
// VALIDATION
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
// HELPERS
import { customStyles, formatDate, handleExportData } from "../../../Utils/helpers/commonhelper";

import { toast } from "sonner";
//css file
import "./ProductServices.css";
import { TableSkeleton } from "../../../Utils/skeletonLoader/TableSkeleton";
// API handlers
import {
  DeleteRequest,
  GetRequest,
  PutRequest,
} from "../../../Utils/hooks/ApiHandler";
import DeleteModal from "../../DeleteModal";
import useLocalStorageState from "use-local-storage-state";
import {
  useGetRequest,
  usePostRequest,
} from "../../../Utils/hooks/QueryHandler";
import { useEffect, useState } from "react";
import Select from "react-select";
import nodata from "../../../Utils/assets/img/leads_no_data_found.png";
import { DescriptionTooltip } from "../../../Utils/helpers/descriptionTooltip/DescriptionTooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFileExport, faFileImport } from "@fortawesome/free-solid-svg-icons";
import masterImage from "../../../Utils/assets/img/masterImage.png";
//Form validation schema using Zod
const schema = z.object({
  service: z.string().min(1, "Product name is required"),
  price: z
    .string()
    .refine((val) => /^[0-9]*\.?[0-9]*$/.test(val), {
      message: "Only numbers and one decimal point allowed",
    })
    .refine((val) => val.length <= 8, {
      message: "Price must be at most 8 characters long",
    }),

  tax: z.enum(["0", "5", "10", "18", "24"], {
    required_error: "Tax is required",
  }),
  description: z.string().optional(),
  hsn_sac: z.string().optional(),
  category: z.enum(["Product", "Service"]).optional(),
  unit: z.string().optional(), // Make it a regular string
})
  .superRefine((data, ctx) => {
    // Custom validation: unit is required only for Product
    if (data.category === "Product" && (!data.unit || data.unit.trim() === "")) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: "Unit is required when category is Product",
        path: ["unit"],
      });
    }
  });
export const ProductServices = () => {
  //  Getting customer info from session storage
  const [customer, setCustomer] = useLocalStorageState("customer");

  //  Local states for managing designation data, errors, and loading state
  const navigate = useNavigate();

  const [products, setProducts] = useState([]);
  const [editId, setEditId] = useState(null);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  // delete modal loading state
  const [loading, setLoading] = useState(false)

  //Auth headers for API requests
  const customerId = customer?.customer_details?.customer_id;
  const userID = customer?.customer_details?.id;

  const token = customer?.token?.authorization;
  const rf = customer?.token?.authentication;
  const headers = {
    authentication: rf,
    Authorization: token,
  };

  const extras = { customer, setCustomer, rf, navigate };
  //React Hook Form setup
  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
    watch,
  } = useForm({
    resolver: zodResolver(schema),

    defaultValues: {
      service: "",
      price: "",
      tax: "5",
      description: "",
      hsn_sac: "",
      category: "Product",
      unit: "",
    },
  });


  // master permissions 
  const permission = customer.customer_details?.permission;
  const user_type = customer.customer_details?.user_type;
  const settingsPermission = permission?.filter(c => c.module_name === "setting")
  // view permission for master 
  const masterGrant = {
    isView: user_type !== 1 ? Boolean(Number(settingsPermission?.find(c => c.feature_name === "master")?.view)) : true,
    isCreate: user_type !== 1 ? Boolean(Number(settingsPermission?.find(c => c.feature_name === "master")?.create)) : true,
    isEdit: user_type !== 1 ? Boolean(Number(settingsPermission?.find(c => c.feature_name === "master")?.edit)) : true,
    isDelete: user_type !== 1 ? Boolean(Number(settingsPermission?.find(c => c.feature_name === "master")?.delete)) : true,
  }

  const productsImportFields = [
    { id: "title", label: "Product & Services", required: true },
    { id: "price", label: "Price", required: true },
    { id: "tax", label: "Tax", required: true },
    { id: "description", label: "Description", required: false },
    { id: "hsn_code", label: "HSC_SAC", req: false },
    { id: "category", label: "Category", req: false },
    { id: "unit", label: "Unit", req: false },
  ];
  //Fetch designations when component mounts
  const {
    data: ProductData,
    error,
    refetch: fetchProducts,
    isLoading,

    isError,
  } = useGetRequest(
    `/masters/getProducts/${customerId}`,
    "getProducts",

    headers,
    extras
  );
  // Fetch departments on component mount
  useEffect(() => {
    if (ProductData?.status === "success") {
      setProducts(ProductData.data);
    } else if (isError) {
      console.error("Error fetching roles:", error);
    }
  }, [ProductData, isError]);

  //Add or update designation

  const { mutate: addProduct } = usePostRequest(
    `/masters/createProduct`,
    null,
    { headers },
    extras
  );

  const onSubmit = async (formData) => {
    if (editId === null && !masterGrant?.isView) {
      toast.warning("You are not authorized for this action!")
      return
    }
    const service = formData.service.trim();
    const description = formData.description?.trim() || "";
    const tax = formData.tax || "";
    const unit = formData.unit || "";
    const hsn_sac = formData.hsn_sac || "";
    const category = formData.category || "";
    const price = formData.price || "";
    //Check for duplicate
    const isDuplicate = products.some(
      (p) => p.service.trim() === service && p.id !== editId
    );
    if (isDuplicate) {
    }

    try {
      const payload = {
        customer_id: customerId,
        service,
        tax,
        unit,
        price,
        hsn_sac,
        description,
        category,
        login_user_id: userID,
      };

      let res;
      if (editId !== null) {
        payload.service_id = editId;

        res = await PutRequest(
          `/masters/updateProduct`,
          payload,
          { headers },
          extras
        );
      } else {
        addProduct(payload, {
          onSuccess: (res) => {
            if (res?.status === "success") {
              toast.success(res.message || "Role added successfully");
              // trigger refetch to fetch all roles again for displaying on UI after successfully adding the role
              fetchProducts();

              reset({
                tax: "5",
                category: "Product",
                price: "",
                hsn_sac: "",
                unit: "",
                description: "",
                service: "",

              });
            } else {
              toast.error(res.message || "Failed to add role");
            }
          },
          onError: (err) => {
            toast.error(err?.message || "Add role request failed");
          },
        });
      }

      if (res?.status === "success") {
        fetchProducts();
        if (editId !== null) setEditId(null);
        reset({
          tax: "5",
          category: "Product",
          price: "",
          hsn_sac: "",
          unit: "",
          description: "",
          service: "",

        });
        toast.success(res.message);
      }
    } catch (err) {
      console.error("Submit error:", err);
      toast.error("Something went wrong while saving.");
    } finally {
    }
  };

  //Set form data in edit mode
  const onEdit = async (item) => {
    try {
      const res = await GetRequest(
        `masters/getProductById/${item.id}`,
        { headers },
        extras
      );
      if (res?.status === "success" && Array.isArray(res.data)) {
        const data = res.data[0];
        setEditId(item.id);
        reset({
          service: data.service || "",
          description: data.description || "",
          price: data.price || "",
          tax: data.tax || "",
          hsn_sac: data.hsn_sac || "",
          category: data.category || "",
          unit: data.unit || "",
        });
      } else {
        throw new Error(res.message || "Invalid response");
      }
    } catch (error) {
      console.error("Error loading details:", error);
      toast.error("Failed to load designation details");
    }
  };
  //Open delete confirmation modal
  const onDelete = (p) => {
    setProductToDelete(p);
    setShowDeleteModal(true);
  };
  //Handle confirmed delete
  const handleConfirmedDelete = async () => {
    if (!productToDelete) return;
    setLoading(true)
    try {
      const res = await DeleteRequest(
        `masters/deleteProduct/${productToDelete.id}`,
        { headers },
        extras
      );

      if (res?.status === "success") {
        const updateProduct = products.filter(
          (p) => p.id !== productToDelete.id
        );
        setProducts(updateProduct);
        await fetchProducts();
        setLoading(false);
        toast.success(res.message);
        setShowDeleteModal(false);
        setProductToDelete(null);
      } else {
        setLoading(false)
        toast.error(res.message);
        // throw new Error(res.message || "Deletion failed");
      }
    } catch (error) {
      setLoading(false)
      console.error("Delete error:", error);
      toast.error("Something went wrong while deleting");
    }
  };

  return (
    <div className="container-fluid p-3 product-container mb-3">
      <DeleteModal
        show={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleConfirmedDelete}
        isLoading={loading}
        message="Are you sure you want to delete this item?"
      />

      <div className="d-flex justify-content-between align-items-center flex-wrap mb-3">
        <div style={{ width: "80%", paddingRight: "20px" }}>
          <h5 className="fw-semibold mb-1">Product And Services</h5>
          <p className="text-muted small">
            Record and Track your business expenditures and get the custom
            reports.
          </p>
        </div>
        <div className="d-flex align-items-center gap-2">
          <Button
            className="btn btn-default-outline"
            onClick={() => navigate(-1)}
          >
            <BsArrowLeft className="me-1" /> Back
          </Button>
          <Dropdown>
            <Dropdown.Toggle className=" btn btn-default no-arrow" style={{ display: "flex", alignItems: "center", paddingRight: "7px" }}>
              Action
              {/* <i className="bi bi-caret-down-fill d-inline-block ps-1" ></i> */}
              <i class="bx bx-chevron-down" style={{ fontSize: "21px" }}></i>
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item
                onClick={() => {
                  navigate("/importdata", {
                    state: {
                      module_name: "master_product",
                      fieldData: [
                        {
                          title: "General Fields",
                          fields: productsImportFields.map((c) => c),
                        },
                      ],
                    },
                  });
                }}><FontAwesomeIcon
                  icon={faFileImport}
                  style={{
                    width: "16px",
                    height: "16px",
                    marginRight: "9px",
                    color: "#5e5f60",
                  }}
                />{" "}
                Import Data</Dropdown.Item>
              <Dropdown.Item onClick={() => {
                  handleExportData([], "master_product", headers, extras);
                }}> <FontAwesomeIcon
                      icon={faFileExport}
                      style={{
                        width: "16px",
                        height: "16px",
                        marginRight: "9px",
                        color: "#5e5f60",
                      }}
                    />{" "}
                    Export Data</Dropdown.Item>
              {/* <Dropdown.Item>Copy from Other</Dropdown.Item>
              <Dropdown.Item>SVC Data</Dropdown.Item> */}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>

      <hr
        className="mb-4"
        style={{ borderTop: "0.5px solid #6c757d", opacity: "0.3", marginTop: "0.7rem" }}
      />

      <div className="row">
        {/* Form Section */}
        <div className="col-md-4 mb-4">
          <div className="form-card">
            <div className="mb-3">
              <h6 className="fw-semibold mb-1">
                {editId ? "Edit Product" : "Add Product"}
              </h6>
              <p className="text-muted small mb-2">
                {editId
                  ? "Update the products & services details below."
                  : "Add new product & services here."}
              </p>
            </div>
            <form onSubmit={handleSubmit(onSubmit)} className="form-container">
              <div className="mb-3   d-flex flex-column focus-z-index position-relative">
                <label className="form-label">
                  Enter Product Name <span className="text-danger">*</span>
                </label>
                <input
                  className="form-control"
                  placeholder="Enter product name"
                  {...register("service")}
                />
                {errors.service && (
                  <small className="text-danger">
                    {errors.service.message}
                  </small>
                )}
              </div>

              <div className="mb-3  d-flex flex-column focus-z-index position-relative">
                <label className="form-label">Enter Product Price</label>
                <input
                  type="text"
                  inputMode="decimal"
                  className="form-control"
                  {...register("price")}
                  onKeyDown={(e) => {
                    const allowedKeys = [
                      "Backspace",
                      "Tab",
                      "ArrowLeft",
                      "ArrowRight",
                      "Delete",
                    ]
                    const isNumber = /^[0-9]$/.test(e.key)
                    const isDot = e.key === "."
                    const alreadyHasDot = e.currentTarget.value.includes(".")

                    if (
                      !allowedKeys.includes(e.key) &&
                      !isNumber &&
                      !(isDot && !alreadyHasDot)
                    ) {
                      e.preventDefault()
                    }
                  }}
                />

                {errors.price && (
                  <small className="text-danger">{errors.price.message}</small>
                )}
              </div>

              <div className="mb-3  d-flex flex-column focus-z-index position-relative">
                <label className="form-label">Tax</label>
                <Controller
                  name="tax"
                  control={control}
                  render={({ field }) => (
                    <Select
                      options={[
                        { value: "0", label: "0%" },
                        { value: "5", label: "5%" },
                        { value: "10", label: "10%" },
                        { value: "18", label: "18%" },
                        { value: "24", label: "24%" },
                      ]}
                      styles={customStyles}
                      classNamePrefix="react-select"
                      placeholder="Select tax"
                      isClearable={true} // user can click "X", but...
                      value={
                        field.value
                          ? { value: field.value, label: `${field.value}%` }
                          : { value: "5", label: "5%" } //  if cleared, fallback to 5%
                      }
                      onChange={(selectedOption) => {
                        field.onChange(selectedOption?.value ?? "5") // fallback to 5%
                      }}
                    />
                  )}
                />
                {errors.tax && (
                  <small className="text-danger">{errors.tax.message}</small>
                )}
              </div>

              <div className="mb-3 d-flex flex-column focus-z-index position-relative">
                <label className="form-label textareaLables">Description</label>
                <textarea
                  className="form-control"
                  rows={3}
                  {...register("description")}
                />
              </div>

              <div className="mb-3  d-flex flex-column focus-z-index position-relative">
                <label className="form-label">HSN or SAC Code</label>
                <input
                  type="text"
                  className="form-control"
                  {...register("hsn_sac")}
                />
                {errors.hsn_sac && (
                  <small className="text-danger">
                    {errors.hsn_sac.message}
                  </small>
                )}
              </div>

              <div className="mb-3  d-flex flex-column">
                {/* <label className="form-label d-block">Category</label> */}

                <span
                  style={{
                    color: "#878b8e",
                    fontSize: "0.86rem",
                    fontWeight: "400",
                    marginBottom: "7px",
                  }}
                >
                  Category
                </span>
                <div className="d-flex gap-4">
                  <div className="d-flex align-items-end gap-2">
                    <Form.Check
                      type="radio"
                      value="Product"
                      id="category-product"
                      {...register("category")}
                      className="custom-radio"
                      style={{ cursor: "pointer" }}
                    />
                    <style jsx>
                      {`
                      .custom-radio .form-check-input {
                        width: 24px;
                        height: 24px;
                        background-color: var(--custom-white);
                        border: 1px solid var(--input-border);
                      }
                      .custom-radio .form-check-input:checked {
                        width: 24px;
                        height: 24px;
                        background-color: var(--primary-color);
                        border-color: var(--primary-color);
                      }
                    `}
                    </style>
                    <span
                      className=""
                      style={{
                        color: "#878b8e",
                        fontSize: "0.86rem",
                        fontWeight: "400",
                      }}
                    >
                      Product
                    </span>
                  </div>

                  <div className="d-flex align-items-end gap-2  ">

                    <Form.Check
                      type="radio"
                      value="Service"
                      id="category-service"
                      {...register("category")}
                      className="custom-radio"
                      style={{ cursor: "pointer" }}
                    />
                    <span
                      style={{
                        color: "#878b8e",
                        fontSize: "0.86rem",
                        fontWeight: "400",
                      }}
                    >
                      Service
                    </span>
                  </div>
                </div>

                {errors.category && (
                  <small className="text-danger d-block">select category</small>
                )}
              </div>
              <div className="mb-3  d-flex flex-column focus-z-index position-relative">
                <label className="form-label">
                  Units
                  {/* Conditionally show asterisk only when category is Product */}
                  {watch("category") === "Product" && <span className="text-danger">*</span>}
                </label>
                <Controller
                  name="unit"
                  control={control}
                  render={({ field }) => (
                    <Select
                      options={[
                        { value: "Kilogram(Kgs)", label: "Kilogram(Kgs)" },
                        { value: "Grams(Gms)", label: "Grams(Gms)" },
                        { value: "Liters(L)", label: "Liters(L)" },
                        { value: "Milliliters(Ml)", label: "Milliliters(Ml)" },
                        { value: "Meters(M)", label: "Meters(M)" },
                        { value: "Centimeters(Cm)", label: "Centimeters(Cm)" },
                        { value: "Dozen(Dz)", label: "Dozen(Dz)" },
                        { value: "Pounds(Lb)", label: "Pounds(Lb)" },
                        { value: "Feet(Ft)", label: "Feet(Ft)" },
                        { value: "Inches(In)", label: "Inches(In)" },
                        { value: "Kilometer(Km)", label: "Kilometer(Km)" },
                        { value: "Box(B)", label: "Box(B)" },
                        { value: "Pieces(P)", label: "Pieces(P)" },
                      ]}
                      styles={customStyles}
                      classNamePrefix="react-select"
                      placeholder="Select unit"
                      isClearable={true}
                      value={
                        field.value
                          ? { value: field.value, label: field.value }
                          : null
                      }
                      onChange={(selectedOption) => {
                        field.onChange(selectedOption?.value ?? "")
                      }}
                    />
                  )}
                />
                {errors.unit && (
                  <small className="text-danger">
                    Please select a valid unit.
                  </small>
                )}
              </div>
              <Button type="submit" className="w-100 btn btn-default">
                {editId ? "Update" : "Submit"}
              </Button>
            </form>
            <hr className="my-3" />
            <div className="text-center mt-4">
              <img
                src={masterImage}
                alt="illustration"
                className="img-fluid"
                style={{ maxHeight: "180px" }}
              />
              <div className="mt-2">
                <h6 className="fw-semibold mb-1">Product And Services</h6>
                <p className="text-muted  mb-0 small ">
                  Record and Track your business expenditures and get the custom
                  reports.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Table Section */}

        <div className="col-md-8">
          <div className="table-card">
            <div className="d-flex justify-content-between align-items-start align-items-md-center flex-column flex-md-row">
              <div>
                <h6 className="fw-semibold mb-1">Product And Services</h6>
                <p className="text-muted small mb-2 mb-md-0">
                  List of current products and services within your
                  organization.
                </p>
              </div>
              {/* <div className="d-flex align-items-center gap-2 mt-1 mb-2 mt-md-0 border border-1 rounded-2 px-2"> */}
              <div className=" position-relative mt-1 mb-2 mt-md-0">
                <input
                  disabled={!masterGrant?.isView}
                  type="search"
                  className="form-control "
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  style={{
                    fontSize: "12px",
                    paddingLeft: "36px",
                    height: "35px",
                  }}
                />
                <i
                  class="fa-solid fa-magnifying-glass"
                  style={{
                    position: "absolute",
                    marginTop: "-26px",
                    zIndex: 1,
                    marginLeft: "13px",
                  }}
                ></i>
              </div>
            </div>

            {error ? (
              <div className="alert alert-danger" role="alert">
                {error?.message || "Something went wrong"}
              </div>
            ) : (
              <>
                {
                  masterGrant?.isView ?
                    <Container
                      fluid
                      style={{
                        fontFamily: "'Poppins', sans-serif",
                        height: "calc(100vh - 10rem)",
                        backgroundColor: "white",
                        padding: 0,
                        animation: "cardEntry 1s ease-out forwards",
                      }}
                      className="mt-3 p-0"
                    >
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          height: "100%",
                          width: "100%",
                        }}
                      >

                        <div
                          className="listStyleForProductandService container-table"
                          style={{
                            flex: products?.length > 10 ? 1 : "auto",
                            overflow: "auto",
                            width: "100%",
                            maxHeight: products?.length > 10 ? "100%" : "auto",
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.scrollbarColor = "#e0e0e0 white"

                          }}
                        >
                          <Table
                            style={{
                              width: "100%",
                              tableLayout: "auto",
                              backgroundColor: "white",
                              marginBottom: "0px",
                            }}
                          >
                            <thead style={{ zIndex: "10" }}>
                              <tr>
                                <th>S.No</th>
                                <th>Title</th>
                                <th>Type</th>
                                <th>Price</th>
                                <th>HSN/SAC</th>
                                <th>Unit</th>
                                <th>Entry Date</th>
                                <th>ACTION</th>
                              </tr>
                            </thead>

                            <tbody>
                              {isLoading ? <TableSkeleton columnCount={8} /> :
                                <>
                                  {products
                                    ?.filter((p) =>
                                      (p.service || "")
                                        .toLowerCase()
                                        .includes(searchTerm.trim().toLowerCase())
                                    )
                                    .map((p, idx) => (
                                      <tr key={p?.id}>
                                        <td>{idx + 1}</td>
                                        <td className="td_position">
                                          <DescriptionTooltip
                                            text={p.service}
                                            maxLength={20}
                                          />
                                        </td>

                                        <td>{p.category}</td>

                                        <td>{p.price}</td>
                                        <td>{p.hsn_sac}</td>
                                        <td>{p.unit ? p.unit : "-"}</td>
                                        <td>{formatDate(p?.created_at)}</td>
                                        <td>
                                          <div className="d-flex gap-2 me-2">
                                            <Button
                                              size="sm"
                                              variant="outline-primary"
                                              onClick={() => {
                                                if (!masterGrant?.isEdit) {
                                                  toast.warning("You are not authorized for this action!")
                                                  return
                                                }
                                                onEdit(p)
                                              }
                                              }
                                              title="Edit"
                                            >
                                              <BsPencil />
                                            </Button>
                                            <Button
                                              size="sm"
                                              variant="outline-danger"
                                              title="Delete"
                                              onClick={() => {
                                                if (!masterGrant?.isDelete) {
                                                  toast.warning("You are not authorized for this action!")
                                                  return
                                                }
                                                onDelete(p)
                                              }}
                                            >
                                              <BsTrash />
                                            </Button>
                                          </div>
                                        </td>
                                      </tr>
                                    ))}
                                  {/*  if no data found on search then it will run  */}
                                  {products.filter((p) =>
                                    (p.service || "")
                                      .toLowerCase()
                                      .includes(searchTerm.trim().toLowerCase())
                                  ).length === 0 && (
                                      <td
                                        colSpan="14"
                                        style={{ textAlign: "center", color: "gray" }}
                                      >
                                        <div
                                          class="d-flex justify-content-center align-items-center"
                                          style={{ position: "absolute", left: " 30%" }}
                                        >
                                          <img
                                            src={nodata}
                                            alt=""
                                            style={{ width: "17rem", marginTop: "0" }}
                                          />
                                        </div>
                                      </td>
                                    )}
                                </>
                              }
                            </tbody>
                          </Table>
                        </div>
                      </div>
                    </Container>
                    :
                    <>You are not authorized to view Products and Services listing!</>
                }

              </>
            )}
          </div>
        </div>
      </div>
    </div>
  )
};